/** @file
 *  Contains the ControlsDialog component used to render a filter icon button and a dialog displaying a
 *  search bar and peer filters for smaller screen sizes
 */

import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    useTheme
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import FiltersGrid from './FiltersGrid';
import ResetFiltersButton from '../buttons/ResetFiltersButton';
import React, { useContext, useState } from 'react';
import { isAnyFilterSet } from '../../functions/filtering';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SearchBar } from '../search/SearchBar';
import { Stack } from '@mui/system';
import {
    FilterStatesContext,
    PEER_FILTER_ACTIONS
} from '../contexts/filterStates';

/**
 * The ControlsDialog component renders a filter icon button which, when clicked,
 * opens a dialog of data filtering controls. Inside the dialog, there's a search bar,
 * a grid of peer filters and a reset button to reset all applied filters.
 * On mobile the dialog displays full-screen.
 *
 * @component
 * @returns {React.ReactElement} The rendered ControlsDialog component.
 */
export const ControlsDialog = () => {
    const filterState = useContext(FilterStatesContext);
    const theme = useTheme();
    const filtersSet = isAnyFilterSet(filterState);

    const [openDialog, setOpenDialog] = useState(false);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };
    return (
        <>
            <IconButton
                id={'controlsDialogIconButton'}
                aria-label='controls dialog'
                onClick={handleOpenDialog}
                style={{
                    backgroundColor: filtersSet
                        ? theme.palette.filters.iconSet
                        : theme.palette.filters.iconUnset,
                    color: filtersSet && 'white'
                }}
                sx={{ boxShadow: 1 }}
            >
                <FilterListIcon />
            </IconButton>
            <Dialog
                id={'controlsDialog'}
                open={openDialog}
                onClose={handleCloseDialog}
                fullScreen={isMobile}
            >
                <DialogContent id={'controlsDialogContent'}>
                    <DialogTitle
                        id={'controlsDialogTitle'}
                        variant='body1'
                        align={'left'}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: 0,
                            paddingRight: 0,
                            paddingTop: 0
                        }}
                    >
                        CONTROLS
                        <IconButton
                            id={'controlsDialogCloseButton'}
                            aria-label='Close'
                            sx={{ marginLeft: 'auto' }}
                            onClick={handleCloseDialog}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <Stack spacing={3}>
                        <SearchBar />
                        <FiltersGrid />
                    </Stack>
                    {filtersSet && (
                        <Box
                            id={'controlsDialogResetFiltersBox'}
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end'
                            }}
                        >
                            <ResetFiltersButton
                                onReset={() => {
                                    filterState.peerFilterDispatch({
                                        type: PEER_FILTER_ACTIONS.CLEAR_FILTERS
                                    });
                                    filterState.setSearchState(null);
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};
